import React, { useMemo } from 'react';
import AutoSizeTitle from '../../components/autoSizeTitle/AutoSizeTitle';
import IndividualInformHeader from '../../components/individualInformHeader/individualInformHeader';
import { getRecommendationMatrices, roundHalfUpInteger } from './selfcareReportLogic';
import './pageRecommendationsSelfcare.css';

export const getRecommendationsSelfcarePageCount = (individualResult) => getRecommendationMatrices(individualResult).length;

// ── Contenido fijo por ID de matriz (spec V5.2 SEC3) ──────────────────────────
const MATRIX_CONTENT = {
    m1_agotamiento_dual: {
        whyPriority: 'Cuando el cuerpo está cansado y las emociones están alteradas simultáneamente, los recursos mentales disponibles para tomar decisiones seguras caen drásticamente. Esta combinación es la que más se asocia con errores de omisión, reacciones impulsivas ante imprevistos y accidentes por pérdida de control en tareas físicas. No es un riesgo futuro — es una condición activa mientras dure el turno.',
        whatCanHappen: 'Impulsividad en tareas con maquinaria, olvidos de verificación, reacción lenta ante emergencias y errores encadenados por desconcentración.',
        operario: [
            'Antes de iniciar cualquier tarea con riesgo de atrapamiento, corte o energía, detén el ritmo 30 segundos y revisa tu estado: ¿estás calmado y alerta? Si no, pide relevo.',
            'Si sientes tensión, rabia o frustración durante el turno, sal del punto de riesgo antes de actuar. Un minuto afuera vale más que una lesión.',
            'Hidratación y alimentación no son opcionales cuando estás en este estado — el cuerpo cansado y sin glucosa toma decisiones más lentas.',
            'No hagas tareas críticas solo cuando estás en este estado. Pide acompañamiento o espera a que llegue tu compañero.',
        ],
        supervisor: [
            'Rotar al operario fuera de tareas críticas de forma inmediata cuando detecte señales de agotamiento o tensión emocional visible.',
            'Implementar buddy system en todas las tareas con energías peligrosas mientras dure este nivel de riesgo.',
            'Reducir la presión de producción sobre este operario específico durante el período de intervención.',
            'Hacer check-in breve al inicio y a mitad del turno: dos preguntas, dos minutos.',
        ],
        sistema: [
            'Revisar si los turnos extendidos o la carga de trabajo actual están generando esta condición de forma sistemática o si es puntual.',
            'Activar protocolo de salud ocupacional o psicología si el patrón se repite en mediciones consecutivas.',
            'Evaluar si el puesto tiene condiciones ergonómicas o de ruido que estén contribuyendo al agotamiento físico.',
            'Programar seguimiento en 30 días para verificar evolución.',
        ],
        intervention: 'Tipo de intervención: CORRECTIVA INMEDIATA',
        actionKey: 'No realizar tareas críticas sin acompañamiento. Pausas, rotación y doble verificación obligatoria.',
        message: 'Hoy tu cuerpo y tu mente necesitan pausa. Habla con tu supervisor antes de continuar.',
    },
    m2_riesgo_silencioso: {
        whyPriority: 'Esta es la combinación más peligrosa para el sistema de seguridad colectivo. Cuando alguien salta pasos y además no comunica los riesgos que ve, genera dos fallas al mismo tiempo: crea condiciones inseguras y las oculta. Los demás no pueden protegerse de algo que desconocen. Los incidentes en esta zona no son accidentes aislados — son fallas sistémicas que se acumulan hasta que algo falla de forma grave.',
        whatCanHappen: 'Riesgos ocultos que nadie más detecta, incidentes que se repiten porque nadie los reportó antes, fallas en cadena que afectan a compañeros que no tienen ninguna señal de alerta.',
        operario: [
            'Si te saltaste un paso hoy, corrígelo ahora antes de seguir. No esperes a que alguien lo note.',
            'La próxima vez que veas algo inseguro, repórtalo aunque creas que es pequeño. Esa observación puede evitar que otro se lastime.',
            'Antes de cada tarea, revisa mentalmente los 3 pasos más críticos del procedimiento. Si no los recuerdas con claridad, consulta antes de actuar.',
            'Si tienes duda de si algo está bien o mal, pregunta. Preguntar no es debilidad — es precisamente lo que evita accidentes.',
        ],
        supervisor: [
            'Implementar verificación de procedimiento al inicio de turno para este operario: no como sanción, como apoyo.',
            'Crear o fortalecer el canal de reporte de condiciones inseguras donde el operario pueda comunicar sin temor a consecuencias.',
            'Revisar si la presión de tiempo o producción está empujando al operario a saltarse pasos.',
            'Observación directa 2 veces por turno durante las próximas 2 semanas.',
        ],
        sistema: [
            'Verificar si los procedimientos están escritos de forma clara y accesible en el puesto.',
            'Revisar si existe un sistema de reporte de condiciones inseguras activo y si los reportes anteriores tuvieron respuesta visible.',
            'Programar micropausas de 5 minutos en tareas largas para restablecer la atención en el procedimiento.',
            'Incluir al operario en actividades de identificación de riesgos — participa más cuando siente que su voz tiene efecto.',
        ],
        intervention: 'Tipo de intervención: PREVENTIVA — Prioritaria',
        actionKey: 'Ambas dimensiones requieren atención. Priorizar comunicación de riesgos. Verificación de procedimiento con acompañamiento.',
        message: 'Si ves algo, dilo. Si te saltaste un paso, corrígelo ahora.',
    },
    m3_rutinizacion: {
        whyPriority: 'La atención en piloto automático es una de las causas más frecuentes de accidentes en tareas repetitivas. Cuando además se pierde el sentido del para qué — la motivación intrínseca de cuidarse — no hay un ancla que active la alerta cuando algo cambia. Esta combinación genera vulnerabilidad sostenida que se acumula silenciosamente con el tiempo. No hay señales de alarma visibles hasta que ocurre el incidente.',
        whatCanHappen: 'Trabajar en automático sin darse cuenta de cambios en las condiciones, omisiones por rutina, menor tolerancia a la frustración en tareas que antes hacía bien, y errores en el momento en que menos se esperan.',
        operario: [
            'Antes de cada tarea, identifica mentalmente qué puede salir diferente hoy respecto a ayer. Una sola pregunta activa la atención.',
            'Cuando notes que tu mente se fue durante el trabajo, para un momento, respira y mira lo que tienes en las manos antes de continuar.',
            'Recuerda por qué trabajas seguro. No es solo cumplir una norma — es llegar a casa a las personas que te esperan.',
            'Si llevas varios días sintiéndote desconectado del trabajo, coméntalo con tu supervisor. No tiene que ser una crisis para merecer atención.',
        ],
        supervisor: [
            'Variar las tareas asignadas a este operario dentro de lo posible — la monotonía extrema agrava el piloto automático.',
            'Hacer una conversación corta y personal (no evaluativa) sobre cómo está el operario — a veces la desconexión viene de afuera.',
            'Incluirlo en decisiones pequeñas del área: ¿cómo organizamos esto mejor? La participación activa la atención.',
            'Verificar que el operario está descansando bien entre turnos — la rutinización se profundiza con fatiga acumulada.',
        ],
        sistema: [
            'Revisar si el diseño del puesto permite alguna variedad de tarea o si es completamente repetitivo sin pausas cognitivas.',
            'Incluir al operario en actividades de bienestar o programas de propósito que conecten su trabajo con el resultado del equipo.',
            'Evaluar condiciones de iluminación, temperatura y ruido — factores ambientales que profundizan la desconexión.',
            'Seguimiento quincenal para verificar evolución de la atención y el compromiso.',
        ],
        intervention: 'Tipo de intervención: PREVENTIVA — Atención focalizada',
        actionKey: 'Reforzar la dimensión más baja. Pausas activas y verificación consciente en puntos críticos de la tarea.',
        message: 'Respira, mira lo que tienes en las manos, y sigue con conciencia.',
    },
    m4_fatiga_atajos: {
        whyPriority: 'Esta es la combinación más directa entre condición física y error procedimental. El cuerpo cansado no toma decisiones de la misma forma que el cuerpo descansado. Cuando la fatiga reduce la capacidad de atención, el primer recurso que sacrifica el cerebro son los pasos que \'parecen innecesarios\' — exactamente los controles de seguridad. Es un mecanismo automático, no una decisión consciente. El predictor más fuerte de accidente por omisión de control.',
        whatCanHappen: 'Saltarse la verificación de EPP, no revisar el estado del equipo antes de usarlo, omitir el bloqueo de energía porque \'va a ser rápido\', y accidentes por condiciones que estaban presentes pero nadie verificó.',
        operario: [
            'Cuando estés cansado, los procedimientos son más importantes, no menos. El cuerpo fatigado comete los errores que el procedimiento previene.',
            'Antes de cada tarea con energías peligrosas, revisa el equipo aunque lo hayas revisado ayer. Las condiciones cambian.',
            'Si sientes que no puedes hacer bien una tarea por el estado en que estás, pídelo — no es queja, es seguridad.',
            'Usa el EPP siempre, incluso en tareas cortas. Los accidentes no esperan a que termines la tarea larga.',
        ],
        supervisor: [
            'Verificar doble (buddy system) en todas las tareas con bloqueo de energía o riesgo de atrapamiento mientras dure este nivel.',
            'Reducir la asignación de tareas críticas a horarios de menor fatiga en el turno — inicio de turno, no al final.',
            'Revisar si este operario lleva turnos extendidos o rotaciones seguidas que estén generando la fatiga.',
            'Acompañamiento cercano durante toda la jornada, no solo verificación al inicio.',
        ],
        sistema: [
            'Revisar el diseño de turnos y cargas de trabajo del área — si varios operarios muestran este patrón, el problema es sistémico.',
            'Evaluar redistribución de funciones o rediseño del puesto para reducir la carga física acumulada.',
            'Activar protocolo de intervención prioritaria: salud ocupacional debe evaluar condición física del operario.',
            'Seguimiento semanal hasta que ambas dimensiones suban del umbral crítico.',
        ],
        intervention: 'Tipo de intervención: CORRECTIVA INMEDIATA',
        actionKey: 'No realizar tareas críticas sin acompañamiento. Doble verificación en puntos de control de energía.',
        message: 'El cansancio no justifica saltarse reglas. El procedimiento existe exactamente para cuando estás cansado.',
    },
    m5_aislamiento_total: {
        whyPriority: 'La seguridad industrial es un sistema colectivo. Cuando alguien no reporta lo que ve y además no verifica que su compañero esté protegido, desconecta dos de las barreras de protección grupal al mismo tiempo. Esto no solo pone en riesgo a quien tiene el déficit — pone en riesgo a todos los que trabajan alrededor. Es una desconexión del sistema de seguridad compartido que requiere intervención antes de que el entorno lo note de la peor manera.',
        whatCanHappen: 'Compañero que trabaja sin protección porque nadie lo verificó, condición insegura que nadie reportó y que termina causando un incidente al siguiente turno, y aislamiento operativo donde cada persona trabaja como si fuera la única en el área.',
        operario: [
            'Antes de arrancar tu turno, verifica que tu compañero inmediato tiene el EPP puesto. Un segundo de verificación puede evitar una lesión.',
            'Si ves algo inseguro — en tu puesto o en el del lado — repórtalo. No tienes que ser el responsable del área para hacer el reporte.',
            'La próxima vez que alguien te diga algo sobre seguridad, escúchalo. La corrección de un compañero es información, no crítica.',
            'Pide ayuda cuando una tarea te exige más de lo que puedes hacer seguro. Eso es responsabilidad, no debilidad.',
        ],
        supervisor: [
            'Crear un ritual de inicio de turno donde los compañeros se verifiquen mutuamente el EPP: dos personas, diez segundos, hábito de equipo.',
            'Generar espacios donde el reporte de condiciones inseguras sea reconocido positivamente frente al grupo.',
            'Revisar si existe alguna dinámica interpersonal en el equipo que esté aislando a este operario.',
            'Observar si el operario participa en las conversaciones de seguridad del área o si permanece al margen.',
        ],
        sistema: [
            'Implementar sistema de reporte de condiciones inseguras con respuesta visible — si los reportes anteriores no tuvieron respuesta, nadie más va a reportar.',
            'Incluir al operario en actividades de identificación de riesgos y mejora del área.',
            'Revisar si la distribución física del puesto aísla al operario del flujo natural del equipo.',
            'Seguimiento quincenal para verificar si mejora la comunicación y la verificación del compañero.',
        ],
        intervention: 'Tipo de intervención: PREVENTIVA — Prioritaria',
        actionKey: 'Ambas dimensiones requieren atención. Verificación del compañero y reporte como hábitos de equipo.',
        message: 'Tu voz protege al equipo. Cuidar al otro es cuidarte a ti.',
    },
};

// ── Metadata de zona (solo colores y etiqueta) ────────────────────────────────
const ZONE_STYLE = {
    green:  { dot: '#1E8449', label: 'ZONA PROTECTORA – Condición protectora' },
    yellow: { dot: '#FDD835', label: 'ZONA MIXTA – Vulnerabilidad parcial' },
    orange: { dot: '#E67E22', label: 'ZONA MIXTA ELEVADA – Riesgo combinado moderado-alto' },
    red:    { dot: '#C0392B', label: 'ZONA CRÍTICA – Alta exposición combinada' },
};

const PageRecommendationsSelfcare = ({ selectedEvaluation, individualResult, orgLogo, startPageNumber }) => {
    const backgroundStyle = {
        backgroundImage: `url('/informs/pagina.png')`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'left top',
        backgroundSize: '100% auto',
    };

    const prioritized = useMemo(() => {
        return getRecommendationMatrices(individualResult);
    }, [individualResult]);

    if (prioritized.length === 0) return null;

    return (
        <>
            {prioritized.map((item, idx) => {
                const pageNumber = startPageNumber + idx;
                const priorityNumber = idx + 1;
                const zone = item.zone;
                const zoneStyle = ZONE_STYLE[zone?.key] || ZONE_STYLE.orange;
                const content = MATRIX_CONTENT[item.def.id];

                return (
                    <div key={`rec-${item.def.id}`} className="pdf-page internal-page">
                        <div className="internal-page-content" style={backgroundStyle}>
                            <AutoSizeTitle text={selectedEvaluation?.evl_title} />

                            <div className="internal-page-info internal-page-info-individual">
                                <IndividualInformHeader individualResult={individualResult} orgLogo={orgLogo} />

                                <div className="selfcare-reco-box">
                                    <div className="selfcare-reco-title">Recomendaciones</div>
                                    <div className="selfcare-reco-subtitle">
                                        Planes de Acción Predictivos
                                    </div>

                                    <div className="selfcare-reco-intro">
                                        Estas acciones se derivan del análisis predictivo por cruce de variables. El objetivo es prevenir
                                        errores y fortalecer hábitos seguros.
                                    </div>

                                    <div className="selfcare-reco-priority">
                                        <span className="selfcare-reco-dot" style={{ backgroundColor: zoneStyle.dot }} />
                                        <span className="selfcare-reco-priority-text">
                                            Prioridad {priorityNumber} – {item.def.matrixTitle}
                                        </span>
                                    </div>

                                    <div className="selfcare-reco-zone">{zoneStyle.label}</div>

                                    <div className="selfcare-reco-why-title">¿Por qué es prioritario?</div>
                                    <div className="selfcare-reco-why">
                                        {content?.whyPriority || item.interpretation}
                                        <br />
                                        <br />
                                        Resultados del cruce: {item.def.xLabel} {item.x == null ? '—' : `${roundHalfUpInteger(item.x)}%`} y{' '}
                                        {item.def.yLabel} {item.y == null ? '—' : `${roundHalfUpInteger(item.y)}%`}.
                                    </div>

                                    <div className="selfcare-reco-section-title">¿Qué puede pasar?</div>
                                    <div className="selfcare-reco-body">{content?.whatCanHappen || item.def.whatCanHappen}</div>

                                    <div className="selfcare-reco-section-title">Acciones recomendadas</div>

                                    <div className="selfcare-reco-actions-grid">
                                        <div>
                                            <div className="selfcare-reco-actions-title">Operario</div>
                                            <ul className="selfcare-reco-bullets">
                                                {(content?.operario || []).map((a, i) => (
                                                    <li key={`op-${i}`}>{a}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <div className="selfcare-reco-actions-title">Supervisor</div>
                                            <ul className="selfcare-reco-bullets">
                                                {(content?.supervisor || []).map((a, i) => (
                                                    <li key={`sup-${i}`}>{a}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div>
                                            <div className="selfcare-reco-actions-title">Sistema / área</div>
                                            <ul className="selfcare-reco-bullets">
                                                {(content?.sistema || []).map((a, i) => (
                                                    <li key={`sis-${i}`}>{a}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="selfcare-reco-footer">{content?.intervention || ''}</div>

                                    <div className="selfcare-reco-body">Acción clave: {content?.actionKey || item.action}</div>

                                    <div className="selfcare-reco-message">
                                        <span className="selfcare-reco-message-label">Mensaje:</span> {content?.message || item.message}
                                    </div>
                                </div>
                            </div>

                            <div className="internal-page-number">{pageNumber}</div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};

export default PageRecommendationsSelfcare;
